import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useModalStore } from "@/stores/modalStore";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import { TbUser, TbInfoCircle, TbShieldCog, TbLogout } from "react-icons/tb";
import { AboutModal, ProfileModal } from "@/components/ui";
import { SupabaseImageService } from "@/services/supabaseImageService";

interface TopNavbarProps {
  className?: string;
}

// Navigation items with optional feature flag mapping
const navigationItems = [
  { label: "Home", path: "/map" },
  { label: "Dashboard", path: "/dashboard" },
  {
    label: "Asset Details",
    path: "/asset-management",
    feature: "asset_management",
  },
  { label: "Reporting", path: "/reporting", feature: "reporting" },
  {
    label: "Bulk assignments",
    path: "/bulk-assignments",
    feature: "bulk_assignments",
  },
];

export const TopNavbar: React.FC<TopNavbarProps> = ({ className = "" }) => {
  const location = useLocation();
  const { user, userProfile, signOut, hasPlatformAccess } = useAuth();
  const { isFeatureEnabled, tenantSettings } = useTenant();
  const {
    isNavDropdownOpen,
    showAboutModal,
    showProfileModal,
    toggleNavDropdown,
    closeNavDropdown,
    openAboutModal,
    closeAboutModal,
    openProfileModal,
    closeProfileModal,
  } = useModalStore();

  const [avatarSignedUrl, setAvatarSignedUrl] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(true);
  const [logoSignedUrl, setLogoSignedUrl] = useState<string | null>(null);

  // Resolve tenant logo path to a signed URL
  useEffect(() => {
    const logoPath = tenantSettings?.logo_url;
    if (!logoPath) {
      setLogoSignedUrl(null);
      return;
    }
    if (logoPath.startsWith("http")) {
      // Legacy full URL or external URL — extract path if it's a storage URL
      if (logoPath.includes("/storage/v1/object/public/task-files/")) {
        const path = logoPath.split(
          "/storage/v1/object/public/task-files/"
        )[1];
        SupabaseImageService.getSignedUrl(path).then((url) =>
          setLogoSignedUrl(url)
        );
      } else {
        setLogoSignedUrl(logoPath);
      }
    } else {
      // Storage path
      SupabaseImageService.getSignedUrl(logoPath).then((url) =>
        setLogoSignedUrl(url)
      );
    }
  }, [tenantSettings?.logo_url]);

  // Resolve avatar storage path to a signed URL
  useEffect(() => {
    const avatarPath = userProfile?.avatar_url;
    if (avatarPath) {
      setAvatarLoading(true);
      SupabaseImageService.getSignedUrl(avatarPath).then((url) => {
        setAvatarSignedUrl(url);
        // Pre-load the image before showing it to avoid the flash
        if (url) {
          const img = new Image();
          img.onload = () => setAvatarLoading(false);
          img.onerror = () => setAvatarLoading(false);
          img.src = url;
        } else {
          setAvatarLoading(false);
        }
      });
    } else {
      setAvatarSignedUrl(null);
      setAvatarLoading(false);
    }
  }, [userProfile?.avatar_url]);

  // Storage key for remembering last active route
  const LAST_ROUTE_KEY = "taskr-last-active-route";

  // Save current route to localStorage when it changes
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingItem = navigationItems.find(
      (item) =>
        item.path === currentPath ||
        (item.path === "/map" && currentPath === "/")
    );

    if (matchingItem) {
      localStorage.setItem(LAST_ROUTE_KEY, matchingItem.path);
    }
  }, [location.pathname]);

  // Get last active route from localStorage
  const getLastActiveRoute = (): string => {
    try {
      return localStorage.getItem(LAST_ROUTE_KEY) || "/map";
    } catch (error) {
      console.warn("Error reading last route from localStorage:", error);
      return "/map";
    }
  };

  const isActiveRoute = (path: string) => {
    if (path === "/map" && location.pathname === "/") return true;
    return location.pathname === path;
  };

  // Check if this is the remembered last active route
  const isLastActiveRoute = (path: string): boolean => {
    const lastRoute = getLastActiveRoute();
    return lastRoute === path;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      closeNavDropdown();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <nav
      className={`bg-white w-full relative rounded-b-xl shadow ${className}`}
    >
      <div className="flex flex-row items-center relative size-full">
        <div className="box-border content-stretch flex flex-row items-center justify-between p-2 relative size-full">
          {/* Left section - Logo and Navigation */}
          <div className="basis-0 box-border content-stretch flex flex-row gap-6 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
            {/* Logo */}
            <Link
              to="/map"
              className="bg-white box-border content-stretch flex flex-row gap-2.5 items-center justify-center px-3.5 py-px relative rounded-[14px] shrink-0 size-10 hover:bg-gray-50 transition-colors"
            >
              {logoSignedUrl ? (
                <img
                  src={logoSignedUrl}
                  alt={tenantSettings?.site_title || "Logo"}
                  className="h-8 max-w-[32px] object-contain"
                  onError={(e) => {
                    // Fallback to default logo on error
                    (e.target as HTMLImageElement).style.display = "none";
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove("hidden");
                  }}
                />
              ) : null}
              <div
                className={`h-8 shrink-0 w-[26px] bg-no-repeat bg-center ${logoSignedUrl ? "hidden" : ""}`}
                style={{
                  backgroundImage: `url('images/logo-mini.svg')`,
                }}
              />
            </Link>

            {/* Navigation Items */}
            <div className="basis-0 box-border content-stretch flex flex-row grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0">
              {navigationItems
                .filter(
                  (item) => !item.feature || isFeatureEnabled(item.feature)
                )
                .map((item) => {
                  const isActive = isActiveRoute(item.path);
                  const isLastActive = isLastActiveRoute(item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`box-border content-stretch flex flex-row gap-2.5 items-center justify-center px-4 py-2.5 relative shrink-0 rounded transition-all duration-200 hover:bg-gray-50 ${
                        isActive
                          ? "text-custom-marian-blue font-semibold bg-blue-50"
                          : isLastActive
                            ? "text-custom-marian-blue font-medium bg-slate-50"
                            : "text-custom-text-gray-dark"
                      } ${
                        isActive
                          ? "border-b-2 border-blue-600"
                          : isLastActive
                            ? "border-b border-blue-300"
                            : "border-b-2 border-transparent"
                      }`}
                    >
                      <span
                        className="font-semibold leading-[1.5] text-[14px] text-left whitespace-nowrap"
                        style={{
                          fontFamily: "Open Sans",
                          fontVariationSettings: "'wdth' 100",
                        }}
                      >
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
            </div>
          </div>

          {/* Right section - User Profile */}
          <div className="relative">
            <button
              onClick={toggleNavDropdown}
              className="box-border cursor-pointer content-stretch flex flex-row gap-5 items-center justify-start pl-2 pr-4 py-2 relative rounded-xl shrink-0 border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="box-border content-stretch flex flex-row gap-3 items-center justify-start p-0 relative shrink-0">
                <div className="relative shrink-0 size-[30px]">
                  {avatarLoading && userProfile?.avatar_url ? (
                    <div className="size-full rounded-full bg-gray-200 animate-pulse" />
                  ) : avatarSignedUrl ? (
                    <img
                      alt="User avatar"
                      className="block max-w-none size-full rounded-full object-cover"
                      height="30"
                      src={avatarSignedUrl}
                      width="30"
                    />
                  ) : (
                    <div className="size-full rounded-full bg-[var(--marian-blue)] flex items-center justify-center text-white font-bold text-[12px]">
                      {(
                        user?.user_metadata?.first_name?.[0] ||
                        user?.email?.[0] ||
                        "U"
                      ).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="box-border content-stretch flex flex-col items-start justify-start pb-0.5 pt-0 px-0 relative shrink-0">
                  <div
                    className="font-bold leading-[1.5] text-custom-text-onyx text-[14px] text-left whitespace-nowrap"
                    style={{
                      fontFamily: "Open Sans",
                      fontVariationSettings: "'wdth' 100",
                    }}
                  >
                    {user?.user_metadata?.first_name || user?.email || "User"}
                  </div>
                </div>
              </div>
              <div className="overflow-clip relative shrink-0 size-4">
                <div className="flex items-center justify-center">
                  <div className="" title="Dropdown arrow">
                    {isNavDropdownOpen ? (
                      <AiOutlineUp
                        size={20}
                        className="block max-w-zone size-full"
                      />
                    ) : (
                      <AiOutlineDown
                        size={20}
                        className="block max-w-zone size-full"
                      />
                    )}
                  </div>
                </div>
              </div>
            </button>

            {/* Dropdown Menu */}
            {isNavDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
                {/* User Info Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    {avatarLoading && userProfile?.avatar_url ? (
                      <div className="shrink-0 size-10 rounded-full bg-gray-200 animate-pulse" />
                    ) : avatarSignedUrl ? (
                      <img
                        alt="User avatar"
                        className="shrink-0 size-10 rounded-full object-cover"
                        src={avatarSignedUrl}
                      />
                    ) : (
                      <div className="shrink-0 size-10 rounded-full bg-[var(--marian-blue)] flex items-center justify-center text-white font-bold text-[14px]">
                        {(
                          user?.user_metadata?.first_name?.[0] ||
                          user?.email?.[0] ||
                          "U"
                        ).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-[#444444] truncate">
                        {user?.user_metadata?.first_name}{" "}
                        {user?.user_metadata?.last_name}
                      </p>
                      <p className="text-[12px] text-[#828282] truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      openProfileModal();
                      closeNavDropdown();
                    }}
                    className="cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#444444] hover:bg-gray-50 transition-colors"
                  >
                    <TbUser size={18} className="text-[#828282]" />
                    Edit Profile
                  </button>
                  <button
                    onClick={() => {
                      openAboutModal();
                      closeNavDropdown();
                    }}
                    className="cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#444444] hover:bg-gray-50 transition-colors"
                  >
                    <TbInfoCircle size={18} className="text-[#828282]" />
                    About
                  </button>
                  {hasPlatformAccess && (
                    <Link
                      to="/platform"
                      onClick={closeNavDropdown}
                      className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#444444] hover:bg-gray-50 transition-colors"
                    >
                      <TbShieldCog size={18} className="text-[#828282]" />
                      Platform Admin
                    </Link>
                  )}
                </div>

                {/* Logout */}
                <div className="border-t border-gray-100 py-1">
                  <button
                    onClick={handleSignOut}
                    className="cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <TbLogout size={18} />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isNavDropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={closeNavDropdown} />
      )}

      {/* Profile Modal */}
      <ProfileModal
        open={showProfileModal}
        onOpenChange={(open) => !open && closeProfileModal()}
      />

      {/* About Modal */}
      <AboutModal
        open={showAboutModal}
        onOpenChange={(open) => !open && closeAboutModal()}
      />
    </nav>
  );
};
